/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { ComponentProps, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { Children, cloneElement, isValidElement } from 'react';
import { cssMap } from '@compiled/react';

import { jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type {
	Media as OriginalMedia,
	MediaGroup as OriginalMediaGroup,
} from '@atlaskit/renderer/nodes';
import { defaultImageCardDimensions } from '@atlaskit/media-card/cardDimensions';

import type { LightNodeProps } from '../LightNodeObserver.ts';

const styles = cssMap({
	group: {
		display: 'flex',
		gap: token('space.100'),
		overflow: 'hidden',
		paddingBlock: token('space.050'),
	},
	card: { flexShrink: 0, width: '156px', height: '125px' },
});

// Progressive serialization can wrap a media element in LightNodeRenderer/mark components.
const getMediaType = (node: ReactNode): string | undefined => {
	let current = node;
	while (isValidElement<PropsWithChildren<{ type?: string }>>(current)) {
		if (current.props.type) return current.props.type;
		current = Children.toArray(current.props.children)[0];
	}
	return undefined;
};

/** Keep a static filmstrip; media loading and scrolling begin on full activation. */
export const MediaGroup = ({
	children,
	lightNodeRef,
}: ComponentProps<typeof OriginalMediaGroup> & LightNodeProps): ReactElement => (
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Preserve Platform renderer media group spacing.
	<div ref={lightNodeRef} className="MediaGroup" css={styles.group}>
		{Children.map(children, (child) =>
			isValidElement<ComponentProps<typeof OriginalMedia>>(child) &&
			getMediaType(child) !== 'link' ? (
				<div css={styles.card}>
					{cloneElement(child, { cardDimensions: defaultImageCardDimensions })}
				</div>
			) : null,
		)}
	</div>
);
